/**
 * @description Component to use OTP boxes
 * @param {number} numberOfBoxes - How many OTP box are needed
 * @param {function} updateValue -  Update OTP value in the caller component to use it anywhere else
 * @param {string} errorMsg - What error message to be printed when needed
 * @param {boolean } disabled - Is the input disabled
 * @param {function} onBlur - Method to handle when onBlur event happens
 * @param {string} otpBoxClassName - Styings to be applied to individual otp box
 * @param {string} otpBoxGroupClassName - Styings to be applied to otp box group
 * @param {string} inputType - What is the input type of the OTP box
 * @param {string} errorMessageClassname - Styings to be applied to error message div
 * @param {string[]} id - ID passed to the input element.
 * @returns {React.FunctionComponent<IOtpBox.IProps>} - Returns JSX for OTP box
 */

import clsx from "clsx";
import React, { useState } from "react";

import {
  BACKSPACE_KEY,
  DELETE_KEY,
  LEFT_ARROW_KEY,
  RIGHT_ARROW_KEY,
  SPACEBAR_KEY,
} from "../../../constants/KeyboardKeys";
import ErrorField from "../ErrorField";
import { IOtpBox } from "./OtpBox";
import SingleOtpBox from "./SingleOtpBox";

const INPUT_TYPE_TEL = "tel";

const OtpBox: React.FunctionComponent<IOtpBox.IProps> = ({
  numberOfBoxes = 4,
  updateValue,
  errorMsg,
  disabled = false,
  onBlur,
  otpBoxClassName = "",
  otpBoxGroupClassName = "",
  errorMessageClassname = "",
  id = [],
  inputType = INPUT_TYPE_TEL,
}) => {
  const [value, setValue] = useState<string[]>([]);
  const [activeInput, setActiveInput] = useState<number>(0);

  const isInputValueValid = (value: string) => {
    const isTypeValid =
      inputType === INPUT_TYPE_TEL ? !isNaN(parseInt(value, 10)) : typeof value === "string";

    return isTypeValid && value.trim().length === 1;
  };

  const changeCodeAtFocus = (newValue: string) => {
    const otp = [...value];
    otp[activeInput] = newValue;
    setValue(otp);
    updateValue(otp.join(""));
  };
  const focusNextInput = () => {
    focusInput(activeInput + 1);
  };

  const focusPrevInput = () => {
    focusInput(activeInput - 1);
  };

  const focusInput = (input: number) => {
    const activeInput = Math.max(Math.min(numberOfBoxes - 1, input), 0);
    setActiveInput(activeInput);
  };
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isInputValueValid(e.target.value)) {
      changeCodeAtFocus(e.target.value);
      focusInput(activeInput + 1);
    }
  };

  const handleOnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.keyCode === BACKSPACE_KEY.key || e.key === BACKSPACE_KEY.name) {
      e.preventDefault();
      changeCodeAtFocus("");
      if (!value[activeInput]) focusPrevInput();
    } else if (e.keyCode === DELETE_KEY.key || e.key === DELETE_KEY.name) {
      e.preventDefault();
      changeCodeAtFocus("");
    } else if (e.keyCode === LEFT_ARROW_KEY.key || e.key === LEFT_ARROW_KEY.name) {
      e.preventDefault();
      focusPrevInput();
    } else if (e.keyCode === RIGHT_ARROW_KEY.key || e.key === RIGHT_ARROW_KEY.name) {
      e.preventDefault();
      focusNextInput();
    } else if (
      e.keyCode === SPACEBAR_KEY.key ||
      e.key === " " ||
      e.key === SPACEBAR_KEY.name ||
      e.key === SPACEBAR_KEY.altName
    ) {
      e.preventDefault();
    }
  };

  const handleOnPaste = (e: ClipboardEvent) => {
    e.preventDefault();

    if (disabled) {
      return;
    }

    let nextActiveInput = activeInput;

    if (e.clipboardData) {
      const pastedData = e.clipboardData
        .getData("text/plain")
        .slice(0, numberOfBoxes - activeInput)
        .split("");

      const valueCopy = [...value];
      for (let pos = 0; pos < numberOfBoxes; ++pos) {
        if (pos >= activeInput && pastedData.length > 0) {
          if (pastedData) {
            valueCopy[pos] = pastedData.shift() ?? "";
            nextActiveInput++;
          }
        }
      }
      setValue(valueCopy);
      updateValue(valueCopy.join(""));
      focusInput(nextActiveInput);
    }
  };

  return (
    <div className={clsx("flex flex-col w-fit", otpBoxGroupClassName)}>
      <div onBlur={onBlur}>
        {Array(numberOfBoxes)
          .fill(undefined)
          .map((_, index) => (
            <SingleOtpBox
              inputType={inputType}
              value={value[index]}
              id={id[index]}
              otpBoxClassName={otpBoxClassName}
              key={index}
              focus={activeInput === index}
              handleOnChange={onChange}
              handleOnFocus={(e: React.ChangeEvent<HTMLInputElement>) => {
                e.target.select();
                setActiveInput(index);
              }}
              handleOnKeyDown={handleOnKeyDown}
              handleOnPaste={handleOnPaste}
              error={errorMsg ? true : false}
              disabled={disabled}
            />
          ))}
      </div>
      {errorMsg && (
        <ErrorField centerAligned errorClassname={errorMessageClassname} errorMessage={errorMsg} />
      )}
    </div>
  );
};
export default OtpBox;
